import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { MtrTypeValue, SupplyByValue } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData, useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListMtrRowData, PriceListMtrRowDataFilterInput } from 'gql/graphql';
import { ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { formatNumberToRu } from 'utils';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import debounce from 'debounce';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { CellRenderCheckbox } from '../AgreementsAddMtr/CellRenderCheckbox';
import {
    getAgreementMtrForIncluding,
    getContractChangeMtrForIncludingCodeFilter,
    getContractChangeMtrForIncludingNameFilter,
    getContractChangeMtrForIncludingUnitFilter,
} from '../AgreementsQueries';

const getYeasOrNo = (flag: boolean | undefined | null) => {
    if (!R.isNil(flag)) {
        return flag ? 'Да' : 'Нет';
    }

    return '';
};

const TITLE = 'Добавление МТР из прейскуранта МТР';
const SEARCH_PLACEHOLDER = 'Поиск МТР в прейскуранте';

const getWhereVariables = createWhereVariablesGetter<PriceListMtrRowDataFilterInput | undefined>({
    searchStringFields: ['code', 'name'],
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useAgreementTableAddMtr = (openModal: boolean) => {
    const { agreementId: agreementIdString } = useParams();
    const { state }: { state: { selected: number } } | undefined = useLocation();

    const [isSubmittedDataTable, setIsSubmittedDataTable] = useState<boolean>(false);

    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [searchString, setSearchString] = useState('');
    const [queryWhere, setQueryWhere] = useState<any>(undefined);

    const agreementId = Number(agreementIdString);

    const variables = useMemo(
        () => ({
            contractChangeId: agreementId,
            where: getWhereVariables(queryWhere),
        }),
        [agreementId, queryWhere],
    );

    const [mtrAgreementsData, mtrAgreementsStatus, mtrAgreementsErrors] = useRequest({
        requestId: 'getAgreementMtrForIncluding',
        query: getAgreementMtrForIncluding,
        variables,
        withEffect: openModal,
    });

    const handleTableFilterUpdated = useCallback((value: { checkedValues: string[]; fieldName: string }) => {
        setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
    }, []);

    const rowData = useMemo(
        () => mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.items || [],
        [mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.items],
    );

    const allCheckedIdData = useMemo(
        () =>
            mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.items?.reduce(
                (acc: Record<number, boolean>, item) => {
                    acc[item.id] = true;

                    return acc;
                },
                {},
            ) || {},
        [mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.items],
    );

    const checkedCount = Object.values(checkedIds).filter((val) => val === true).length;

    const isAllRowChecked = useMemo(
        () => checkedCount === mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.totalCount,
        [checkedCount, mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange?.totalCount],
    );

    const handleChangeCheckbox = useCallback(
        (rowId: number, isHeader: boolean) => (event: { checked: boolean }) => {
            const { checked } = event;

            if (isHeader) {
                setCheckedIds(() => (isAllRowChecked ? {} : allCheckedIdData));
            } else {
                setCheckedIds((prev) => ({
                    ...prev,
                    [rowId]: checked,
                }));
            }
        },
        [allCheckedIdData, isAllRowChecked],
    );

    const columns = useMemo<ColDef<PriceListMtrRowData>[] | ColGroupDef<PriceListMtrRowData>[]>(
        () => [
            {
                field: 'menu' as any,
                lockPosition: 'left',
                headerName: '',
                width: 55,
                headerComponent: CellRenderCheckbox,
                headerComponentParams: {
                    checkedIds,
                    handleChangeCheckbox,
                    isHeadCheckbox: true,
                    checkedCount,
                    isAllRowChecked,
                    hasDataItems: !!rowData.length,
                },
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox,
                },
                resizable: false,
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 136,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeMtrForIncludingCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeMtrForIncludingCodeFilter,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    dataAccessor: 'uerContractChangeMtrForIncludingNameFilter',
                    changeFilterHandler: handleTableFilterUpdated,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeMtrForIncludingNameFilter,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед. изм.',
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    dataAccessor: 'uerContractChangeMtrForIncludingUnitFilter',
                    changeFilterHandler: handleTableFilterUpdated,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Ед. изм.',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeMtrForIncludingUnitFilter,
                },
            },
            {
                field: 'unitCost',
                headerName: 'Стоимость единицы, руб. без ндс',
                valueFormatter: ({ data }) => formatNumberToRu(data?.unitCost, 2),
                width: 136,
            },
            {
                field: 'supplyBy',
                headerName: 'Поставка',
                valueFormatter: ({ data }) => (data?.supplyBy ? SupplyByValue[data?.supplyBy] : ''),
                width: 136,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    changeFilterHandler: handleTableFilterUpdated,
                    filterItems: [
                        {
                            label: SupplyByValue[ContragentTypeEnum.Contractor],
                            value: ContragentTypeEnum.Contractor,
                        },
                        {
                            label: SupplyByValue[ContragentTypeEnum.Customer],
                            value: ContragentTypeEnum.Customer,
                        },
                    ],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Поставка',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                },
            },
            {
                field: 'mtrType',
                headerName: 'Тип',
                valueFormatter: ({ data }) => (data?.mtrType ? MtrTypeValue[data?.mtrType] : ''),
                width: 136,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    filterItems: [
                        {
                            label: MtrTypeValue[MtrTypeEnum.Material],
                            value: MtrTypeEnum.Material,
                        },
                        {
                            label: MtrTypeValue[MtrTypeEnum.Equipment],
                            value: MtrTypeEnum.Equipment,
                        },
                    ],
                    changeFilterHandler: handleTableFilterUpdated,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 85,
                        cellRenderer: ({ data }: { data: PriceListMtrRowData }) => (
                            <>{getYeasOrNo(data?.isIncludedInWorkOrder)}</>
                        ),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 85,
                        cellRenderer: ({ data }: { data: PriceListMtrRowData }) => (
                            <>{getYeasOrNo(data?.isIncludedInAct)}</>
                        ),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 95,
                        cellRenderer: ({ data }: { data: PriceListMtrRowData }) => (
                            <>{getYeasOrNo(data?.isIncludedInWorkOrderChange)}</>
                        ),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
        ],
        [
            agreementId,
            checkedCount,
            checkedIds,
            handleChangeCheckbox,
            handleTableFilterUpdated,
            isAllRowChecked,
            rowData.length,
        ],
    );

    const [refreshFiltersData] = useResetMultipleStoreData(
        useMemo(
            () => ({
                requestIds: [
                    'contractChangeMtrForIncludingCodeFilter',
                    'contractChangeMtrForIncludingNameFilter',
                    'contractChangeMtrForIncludingUnitFilter',
                ],
            }),
            [],
        ),
    );
    const [resetAgreementMtrForIncluding] = useResetStoreData({ requestId: 'getAgreementMtrForIncluding' });

    const handleCloseErrorAgreementTable = useCallback(() => setIsSubmittedDataTable(false), []);

    const handleSearch = useCallback(({ value }) => {
        setQueryWhere((prevState: AnyObject) => ({
            ...prevState,
            searchString: value,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const handleClearSearch = useCallback(() => {
        if (searchString?.length) {
            setSearchString('');
            setQueryWhere((prevFilters: AnyObject) => ({
                ...prevFilters,
                searchString: undefined,
            }));
        }
    }, [searchString?.length]);

    useEffect(() => {
        if (state?.selected) {
            setCheckedIds((ids) => ({ ...ids, [state?.selected]: true }));
        }
    }, [handleChangeCheckbox, setCheckedIds, state?.selected]);

    useEffect(() => {
        setIsSubmittedDataTable(true);

        if (mtrAgreementsStatus === RequestStatus.SUCCESS && !mtrAgreementsErrors) {
            setIsSubmittedDataTable(false);
        }
    }, [mtrAgreementsErrors, mtrAgreementsStatus]);

    useEffect(() => {
        if (!openModal) {
            resetAgreementMtrForIncluding(undefined);
            setCheckedIds({});
            refreshFiltersData();
        }
    }, [openModal, refreshFiltersData, resetAgreementMtrForIncluding]);

    useEffect(() => {
        if (!openModal) {
            setQueryWhere({});
        }
    }, [openModal]);

    return {
        titleModal: TITLE,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        agreementId,
        checkedIds,
        setCheckedIds,
        searchString,
        agreementsData: mtrAgreementsData?.uerPriceListMtrForIncludingToContractChange,
        agreementsStatus: mtrAgreementsStatus,
        agreementsErrors: mtrAgreementsErrors,
        finalColumns: columns,
        isSubmittedDataTable,
        handleCloseErrorAgreementTable,
        handleTableFilterUpdated,
        handleSearch,
        handleChange,
        handleClearSearch,
    };
};
